import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import { XButton } from '../../../components/XButton';
import XDateField from '../../../components/XDateField';
import { healthCheckProxyDataProvider } from './dataProvider';
import { HealthCheckProxy } from './types';
import { PermissionButton } from 'src/routes/components/permission-button';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';

const T = 'module.healthCheckProxy';

const PROTOCOL_OPTIONS = ['http', 'https', 'socks4', 'socks5'] as const;
const PROXY_HOST_REGEX =
  /^(localhost|(([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,})|(\d{1,3}\.){3}\d{1,3})$/;

// ---------------------------------------------------------------------------
// Add / Edit dialog
// ---------------------------------------------------------------------------

type ProxyFormData = {
  protocol: string;
  host: string;
  port: string;
  username: string;
  password: string;
  enabled: boolean;
};

type ProxyDialogProps = {
  open: boolean;
  initial?: HealthCheckProxy | null;
  loading: boolean;
  onClose: () => void;
  onConfirm: (data: { protocol: string; host: string; port: number; username: string; password: string; enabled: boolean }) => void;
};

function ProxyDialog({ open, initial, loading, onClose, onConfirm }: ProxyDialogProps) {
  const translate = useTranslate();
  const [form, setForm] = useState<ProxyFormData>({
    protocol: '',
    host: '',
    port: '',
    username: '',
    password: '',
    enabled: true,
  });
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        protocol: initial?.protocol ?? '',
        host: initial?.host ?? '',
        port: initial?.port != null ? String(initial.port) : '',
        username: initial?.username ?? '',
        password: initial?.password ?? '',
        enabled: initial?.enabled ?? true,
      });
      setTouched(false);
    }
  }, [open, initial]);

  const protocolError = touched && !form.protocol;
  const hostError = touched && !PROXY_HOST_REGEX.test(form.host.trim());
  const portError = touched && (!form.port || isNaN(Number(form.port)) || Number(form.port) <= 0 || Number(form.port) > 65535);
  const usernameError = touched && !form.username.trim();
  const passwordError = touched && !form.password;

  const handleConfirm = () => {
    setTouched(true);
    if (
      !form.protocol ||
      !PROXY_HOST_REGEX.test(form.host.trim()) ||
      !form.port ||
      isNaN(Number(form.port)) ||
      Number(form.port) <= 0 ||
      Number(form.port) > 65535 ||
      !form.username.trim() ||
      !form.password
    ) return;
    onConfirm({
      protocol: form.protocol.trim(),
      host: form.host.trim(),
      port: Number(form.port),
      username: form.username.trim(),
      password: form.password,
      enabled: form.enabled,
    });
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {translate(initial ? `${T}.editProxy` : `${T}.addProxy`)}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            fullWidth
            size="small"
            label={translate(`${T}.protocol`)}
            value={form.protocol}
            onChange={(e) => { setForm((f) => ({ ...f, protocol: e.target.value })); setTouched(true); }}
            error={protocolError}
            helperText={protocolError ? translate(`${T}.invalidProtocol`) : undefined}
          >
            {PROTOCOL_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={translate(`${T}.host`)}
            value={form.host}
            onChange={(e) => { setForm((f) => ({ ...f, host: e.target.value })); setTouched(true); }}
            error={hostError}
            helperText={hostError ? translate(`${T}.invalidHost`) : undefined}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label={translate(`${T}.port`)}
            value={form.port}
            onChange={(e) => { setForm((f) => ({ ...f, port: e.target.value })); setTouched(true); }}
            error={portError}
            helperText={portError ? translate(`${T}.invalidPort`) : undefined}
          />
          <TextField
            fullWidth
            size="small"
            label={translate(`${T}.username`)}
            value={form.username}
            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
            error={usernameError}
            helperText={usernameError ? translate(`${T}.requiredUsername`) : undefined}
          />
          <TextField
            fullWidth
            size="small"
            type="password"
            label={translate(`${T}.password`)}
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            error={passwordError}
            helperText={passwordError ? translate(`${T}.requiredPassword`) : undefined}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
            }
            label={translate(`${T}.enabled`)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {translate('ra.action.cancel')}
        </Button>
        <LoadingButton variant="contained" loading={loading} onClick={handleConfirm}>
          {translate('ra.action.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main List
// ---------------------------------------------------------------------------

export default function HealthCheckProxyList() {
  const notify = useNotify();
  const translate = useTranslate();

  const [records, setRecords] = useState<HealthCheckProxy[]>([]);
  const [loadingList, setLoadingList] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [editing, setEditing] = useState<HealthCheckProxy | null>(null);

  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const result = await healthCheckProxyDataProvider.getList('', {
        pagination: { page: 1, perPage: 200 },
        sort: { field: 'createdAt', order: 'DESC' },
        filter: {},
      });
      setRecords(result.data);
    } catch (e: any) {
      notify(e?.message || translate(`${T}.notify.loadError`), { type: 'error' });
    } finally {
      setLoadingList(false);
    }
  }, [notify, translate]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleOpenAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (row: HealthCheckProxy) => {
    setEditing(row);
    setDialogOpen(true);
  };

  const handleDialogConfirm = async (data: { protocol: string; host: string; port: number; username: string; password: string; enabled: boolean }) => {
    setDialogLoading(true);
    try {
      if (editing) {
        await healthCheckProxyDataProvider.update('', { id: editing.id, data });
        notify(translate(`${T}.notify.updated`), { type: 'success' });
      } else {
        await healthCheckProxyDataProvider.create('', { data });
        notify(translate(`${T}.notify.created`), { type: 'success' });
      }
      setDialogOpen(false);
      fetchList();
    } catch (e: any) {
      notify(e?.message || translate(`${T}.notify.operationFailed`), { type: 'error' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(translate(`${T}.confirm.delete`))) return;
    setDeleteLoading(id);
    try {
      await healthCheckProxyDataProvider.delete('', { id });
      notify(translate(`${T}.notify.deleted`), { type: 'success' });
      fetchList();
    } catch (e: any) {
      notify(e?.message || translate(`${T}.notify.deleteFailed`), { type: 'error' });
    } finally {
      setDeleteLoading(null);
    }
  };

  return (
    <PermissionGuard resource="domain-configs" action="read" fallback={<ErrorPage403 />}>
    <DashboardContent>
      <CustomBreadcrumbs
        heading={translate(`${T}.title`)}
        links={[{ name: translate(`${T}.title`) }]}
        action={
          <Stack direction="row" spacing={1}>
            <PermissionButton resource="domain-configs" action="create">
              <XButton
                variant="contained"
                label={translate(`${T}.addProxy`)}
                onClick={handleOpenAdd}
                sx={{ minWidth: 120 }}
              />
            </PermissionButton>
          </Stack>
        }
      />

      <Card sx={{ mt: 3 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate(`${T}.protocol`)}</TableCell>
                <TableCell>{translate(`${T}.host`)}</TableCell>
                <TableCell>{translate(`${T}.port`)}</TableCell>
                <TableCell>{translate(`${T}.username`)}</TableCell>
                <TableCell>{translate(`${T}.enabled`)}</TableCell>
                <TableCell>{translate(`${T}.createdAt`)}</TableCell>
                <TableCell>{translate(`${T}.updatedAt`)}</TableCell>
                <TableCell align="right">{translate(`${T}.actions`)}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingList && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {translate(`${T}.loading`)}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loadingList && records.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {translate(`${T}.noRecords`)}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {records.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.protocol}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {row.host}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.port}</TableCell>
                  <TableCell>{row.username}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={translate(row.enabled ? `${T}.enabled` : `${T}.disabled`)}
                      color={row.enabled ? 'success' : 'default'}
                      variant="soft"
                    />
                  </TableCell>
                  <TableCell>
                    <XDateField fullDate value={row.createdAt} />
                  </TableCell>
                  <TableCell>
                    <XDateField fullDate value={row.updatedAt} />
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" gap={0.5}>
                      <PermissionButton resource="domain-configs" action="update">
                        <Tooltip title={translate('ra.action.edit')}>
                          <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                            <Iconify icon="solar:pen-bold" width={16} />
                          </IconButton>
                        </Tooltip>
                      </PermissionButton>
                      <PermissionButton resource="domain-configs" action="delete">
                        <Tooltip title={translate('ra.action.delete')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(row.id)}
                            disabled={deleteLoading === row.id}
                          >
                            <Iconify icon="solar:trash-bin-trash-bold" width={16} />
                          </IconButton>
                        </Tooltip>
                      </PermissionButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <ProxyDialog
        open={dialogOpen}
        initial={editing}
        loading={dialogLoading}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleDialogConfirm}
      />
    </DashboardContent>
    </PermissionGuard>
  );
}
